/**
 * A table of nys
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import NYSTableRow from "./nys-table-row";
import nysTableColumns from "../../../data/nys-table-columns";

/**
 * Compare two numeric values for sorting
 */
const compareValues = (a, b, direction) => {
  if (a < b) {
    return direction === "ascending" ? -1 : 1;
  }
  if (a > b) {
    return direction === "ascending" ? 1 : -1;
  }
  return 0;
};

/**
 * Get expected quantity (manifest or received fallback) for sorting
 */
const getExpectedQty = (item) => {
  return typeof item.manifestQuantity === 'number'
    ? item.manifestQuantity
    : typeof item.wmsQuantityReceived === 'number'
      ? item.wmsQuantityReceived
      : 0;
};

/**
 * Get stowed quantity for sorting
 */
const getStowedQty = (item) => {
  return typeof item.wmsQuantityReceivedStow === 'number' ? item.wmsQuantityReceivedStow : 0;
};

/**
 * Get NYS backlog quantity for sorting (expected - stowed)
 */
const getNYSBacklogQty = (item) => {
  return Math.max(0, getExpectedQty(item) - getStowedQty(item));
};

/**
 * Create comparator function for NYS table sorting
 */
const createNYSComparator = (sortColumn, sortDirection) => {
  return (a, b) => {
    if (sortColumn === "transConfirmedUnits") {
      return compareValues(getExpectedQty(a), getExpectedQty(b), sortDirection);
    }

    if (sortColumn === "transReceivedUnits") {
      return compareValues(getStowedQty(a), getStowedQty(b), sortDirection);
    }

    if (sortColumn === "backlog") {
      return compareValues(getNYSBacklogQty(a), getNYSBacklogQty(b), sortDirection);
    }

    // Handle all other columns with standard sorting
    const aSortColumn = a[sortColumn];
    const bSortColumn = b[sortColumn];

    // Handle numbers
    if (typeof aSortColumn === 'number' && typeof bSortColumn === 'number') {
      return compareValues(aSortColumn, bSortColumn, sortDirection);
    }

    // Handle strings
    const aStr = String(aSortColumn || '');
    const bStr = String(bSortColumn || '');
    if (aStr < bStr) {
      return sortDirection === "ascending" ? -1 : 1;
    }
    if (aStr > bStr) {
      return sortDirection === "ascending" ? 1 : -1;
    }
    return 0;
  };
};
const NYSTable = props => {
  const { nysData, criticalAsinEvents, region_id } = props;
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("ascending");

  const onSort = ({ sortColumn, sortDirection }) => {
    setSortDirection(sortDirection)
    setSortColumn(sortColumn)
  };
  // Make a copy of incoming shipments and sort if `sortColumn` exists
  const sortedNysItems = [...nysData];
  if (sortColumn) {
    sortedNysItems.sort(createNYSComparator(sortColumn, sortDirection));
  }

  const visibleNysItems = sortedNysItems.slice(
    props.visibleIndices[0],
    props.visibleIndices[1]
  );

  return (
    <React.Fragment>
      <Table
        headerRows={1}
        showDividers={true}
        rowComponents={[NYSTableRow, TableRow]}
        stickyHeaderRow={true}
        stickyHeaderColumn={false}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showStripes={true}
      >
        <TableRow>
          {nysTableColumns
            .map(header => (
              <TableCell
                key={header.id}
                sortColumn={header.id}
                width={header.width}
                alignmentHorizontal={header.alignment}
              >
                {header.label}
              </TableCell>
            ))}
        </TableRow>
        {visibleNysItems.map((nysItem, index) => (
          <NYSTableRow
            key={`${nysItem.toteId}-${nysItem.asin}-${index}`}
            nysItem={nysItem}
            criticalAsinEvents={criticalAsinEvents}
            region_id={region_id}
          />
        ))}
      </Table>
    </React.Fragment>
  );
};

NYSTable.propTypes = {
  visibleIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
  nysData: PropTypes.arrayOf(PropTypes.object).isRequired,
  criticalAsinEvents: PropTypes.object.isRequired,
  region_id: PropTypes.number.isRequired,
};

export default NYSTable;
